import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { request } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import CustomBackdrop from '../_common/CustomBackdrop'
import CustomDialog from '../_common/CustomDialog'
import UsersItem from './UsersItem'
import UserAdd from './UserAdd'
import UserEdit from './UserEdit'
import UserDelete from './UserDelete'

const UsersWrapper = (props) => {
  const theme = useTheme()

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [isLoading, setIsLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [editUser, setEditUser] = useState(null)
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false)
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false)
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false)
  const [fetchUsers, setFetchUsers] = useState(1)

  const pageLabelStyle = {
    color: theme.palette.vodka,
    fontSize: '18px',
    fontWeight: '500',
    padding: '0px 0px 20px 20px',
  }

  const wrapperStyle = {
    background: theme.palette.aliceBlue,
    borderRadius: '20px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    padding: '20px',
    minHeight: '500px',
  }

  const addButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    textTransform: 'capitalize',
    height: '50px',
    borderRadius: '25px',
    padding: '0px 20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const handleAddUserDialogOpen = () => {
    setAddUserDialogOpen(true)
  }

  const handleAddUserDialogClose = () => {
    setAddUserDialogOpen(false)
  }

  const handleEditUserDialogOpen = (user_id) => {
    setEditUserDialogOpen(true)
    setEditUser(users.find(item => item.id === user_id))
  }

  const handleEditUserDialogClose = () => {
    setEditUserDialogOpen(false)
    setEditUser(null)
  }

  const handleDeleteUserDialogOpen = (user_id) => {
    setDeleteUserDialogOpen(true)
    setEditUser(users.find(item => item.id === user_id))
  }

  const handleDeleteUserDialogClose = () => {
    setDeleteUserDialogOpen(false)
    setEditUser(null)
  }

  useEffect(() => {
    const fetchUsersData = async () => {
      setIsLoading(true)

      const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsers      
      const request_data = { url, method: 'GET' }    
      const response = await request(request_data)
      
      if (response && response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        setUsers(response.data)
      } else {
        setUsers([])
      }

      setIsLoading(false)
    }

    fetchUsersData()
  }, [fetchUsers])
  
  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Box sx={{ padding: '40px 20px' }}>

        <Box sx={pageLabelStyle}>{defaultTexts.users}</Box>

        <Box sx={wrapperStyle}>

          {
            editPermission &&
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-end'
              sx={{ marginBottom: '30px' }}
            >
              <Button
                variant='contained'
                disableRipple
                disableElevation
                onClick={handleAddUserDialogOpen}
                startIcon={<AddCircleOutlineIcon />}
                sx={addButtonStyle}
              >
                {defaultTexts.addNewUser}
              </Button>
            </Stack>
          }          

          {
            users.map((item, index) => (
              <UsersItem
                key={index}
                {...item}
                handleEditUserDialogOpen={handleEditUserDialogOpen}
                handleDeleteUserDialogOpen={handleDeleteUserDialogOpen}
              />
            ))
          }

        </Box>

      </Box>

      <CustomDialog
        open={addUserDialogOpen}
        onClose={handleAddUserDialogClose}
        dialogTitle={{
          title: defaultTexts.addNewUser,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          },
        }}
        closeIconColor={theme.palette.violetsAreBlue}
			>
        <UserAdd
          handleAddUserDialogClose={handleAddUserDialogClose}
          setFetchUsers={setFetchUsers}
        />
      </CustomDialog>

      <CustomDialog
        open={editUserDialogOpen}
        onClose={handleEditUserDialogClose}
        dialogTitle={{
          title: defaultTexts.editUser,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          },
        }}
        closeIconColor={theme.palette.violetsAreBlue}
			>
        <UserEdit
          handleEditUserDialogClose={handleEditUserDialogClose}
          editUser={editUser}
          setFetchUsers={setFetchUsers}
        />
      </CustomDialog>

      <CustomDialog
        open={deleteUserDialogOpen}
        onClose={handleDeleteUserDialogClose}
        dialogTitle={{
          title: defaultTexts.deleteUser,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          },
        }}
        closeIconColor={theme.palette.violetsAreBlue}
			>
        <UserDelete
          handleDeleteUserDialogClose={handleDeleteUserDialogClose}
          editUser={editUser}
          setFetchUsers={setFetchUsers}
        />
      </CustomDialog>
    </>
  )
}

export default UsersWrapper